
import { useState, useEffect } from "react";
import { baseUrl } from "../../config";
import axios from "axios";
import { 
  Row, 
  Col, 
  Button, 
  Space, 
  Upload, 
  notification, 
  Form, 
  Input, 
  Select, 
  Table, 
  Collapse, 
  Image, 
  Modal, 
  AutoComplete, 
  Popconfirm,
  Card,
  Typography,
  Divider,
  Empty,
  Tooltip,
  Badge,
  Alert,
  Spin
} from "antd";
import '../../assets/styles/custom_style.css';
import { 
  DeleteOutlined, 
  EditOutlined, 
  UploadOutlined, 
  EyeOutlined, 
  PlusOutlined,
  TableOutlined,
  BankOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  ClearOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined
} from "@ant-design/icons";
import PdfImage from "../../assets/images/document.png";
import DocImage from "../../assets/images/docs.png";
import XlsxImage from "../../assets/images/xlsx.png";

const { Item } = Form;
const { Option } = Select;
const { Panel } = Collapse;
const { confirm } = Modal;
const { Title, Text } = Typography;

const OccupationDetails = ({ user_id }) => {
  console.log("user_id for occupation:", user_id);
  
  const [form] = Form.useForm();
  const [recordId, setRecordId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userData, setUserData] = useState([]);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optionSuggestion, setOptionSuggestion] = useState([]);
  
  // New state for better UX
  const [viewMode, setViewMode] = useState('table'); // 'table', 'form', 'empty'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    setUserData([]);
    setViewMode('empty');
    setLoading(true);
    
    if (user_id) {
      fetchOccupationData(user_id);
    } else {
      setLoading(false);
    }
  }, [user_id]);

  const fetchOccupationData = async (user_id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/occupation/list/${user_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });
      
      if (response.data.status) {
        const occupationData = response.data.result || [];
        setUserData(occupationData);
        setViewMode(occupationData.length > 0 ? 'table' : 'empty');
      } else {
        notification.info({
          message: 'Info',
          description: response.data.message,
          placement: 'topRight'
        });
        setViewMode('empty');
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to fetch occupation data',
        placement: 'topRight'
      });
      setViewMode('empty');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    // Validate user_id
    if (!user_id) {
      notification.warning({
        message: 'Warning',
        description: "Please save member basic information first before adding occupation details.",
        placement: 'topRight'
      });
      return;
    }

    // Check if at least one field is filled
    const isEmpty = Object.values(values).every(x => x === null || x === '' || x === undefined);
    if (isEmpty && fileList.length === 0) {
      notification.info({
        message: 'Info',
        description: "Please fill out at least one field or upload a document.",
        placement: 'topRight'
      });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      
      // Append form values
      Object.keys(values).forEach(key => {
        formData.append(key, values[key] ? values[key] : '');
      });

      // Append files
      if (fileList.length > 0) {
        fileList.forEach(file => {
          const blob = new Blob([file.binary], { type: file.type });
          formData.append('multiFiles', blob, file.name);
        });
      }

      formData.append("user_id", user_id);

      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/occupation/update/${recordId}`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      } else {
        response = await axios.post(`${baseUrl}/occupation/create`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode 
            ? 'Occupation details updated successfully!' 
            : 'Occupation details added successfully!',
          placement: 'topRight'
        });
        
        // Reset form and refresh data
        handleFormReset();
        await fetchOccupationData(user_id);
        setViewMode('table');
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to save occupation details',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'An error occurred while saving',
        placement: 'topRight'
      });
    } finally {
      setSaving(false);
    }
  };

  const beforeUpload = (file) => {
    // Validate file size (max 10MB)
    const isValidSize = file.size / 1024 / 1024 < 10;
    if (!isValidSize) {
      notification.error({
        message: 'File too large',
        description: 'File size must be less than 10MB',
        placement: 'topRight'
      });
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const newFile = { 
        name: file.name, 
        binary: e.target.result, 
        type: file.type,
        uid: file.uid || Date.now() 
      };
      setFileList(prevState => [...prevState, newFile]);
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent automatic upload
  };

  const handleEdit = (record) => {
    // Populate form with record data
    form.setFieldsValue({
      occ_name: record.occ_name || '',
      occ_field: record.occ_field || '',
      occ_category: record.occ_category || '',
      other_spec: record.other_spec || '',
      shop_no: record.shop_no || '',
      building_name: record.building_name || '',
      occ_street: record.occ_street || '',
      occ_locality: record.occ_locality || '',
      occ_district: record.occ_district || '',
      occ_state: record.occ_state || '',
      occ_country: record.occ_country || '',
      occ_zip_code: record.occ_zip_code || '',
      occ_office_phone_no: record.occ_office_phone_no || '',
      occ_mobile_no: record.occ_mobile_no || '',
    });
    
    setRecordId(record.id);
    setSelectedRecord(record);
    setIsUpdateMode(true);
    setViewMode('form');
  };

  const handleDelete = async (record) => {
    try {
      const response = await axios.delete(`${baseUrl}/occupation/delete/${record.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: 'Occupation deleted successfully!',
          placement: 'topRight'
        });
        await fetchOccupationData(user_id);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to delete occupation',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete occupation',
        placement: 'topRight'
      });
    }
  };

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Delete Occupation Details',
      content: `Are you sure you want to delete the occupation "${record.occ_name}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record);
      },
    });
  };

  const deleteImage = async (id, imagePath) => {
    try {
      const response = await axios.delete(`${baseUrl}/occupation/deleteImage?id=${id}&imagePath=${imagePath}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: 'Document deleted successfully!',
          placement: 'topRight'
        });
        await fetchOccupationData(user_id);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to delete document',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Error',
        description: 'Failed to delete document',
        placement: 'topRight'
      });
    }
  };

  const handleExpand = (record) => {
    const key = record.id;
    setExpandedRowKeys(expandedRowKeys.includes(key) ? [] : [key]);
  };

  const handleFormReset = () => {
    form.resetFields();
    setFileList([]);
    setRecordId(null);
    setSelectedRecord(null);
    setIsUpdateMode(false);
  };

  const handleViewChange = (newView) => {
    if (newView === 'form') {
      handleFormReset();
    }
    setViewMode(newView);
  };

  const getPanelValue = async (searchText, columnName, tableName) => {
    if (searchText.length < 3) {
      setOptionSuggestion([]);
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/payments/getSuggestionValue/`, {
        params: { q: searchText, column: columnName, table: tableName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        setOptionSuggestion(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleChangeSuggestion = (value, columnName) => {
    form.setFieldsValue({
      [columnName]: value
    });
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ color: '#1890ff', fontSize: 16 }} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
      default:
        return <FileImageOutlined style={{ color: '#722ed1', fontSize: 16 }} />;
    }
  };

  const renderFilePreview = (item, recordId) => {
    const isPdf = item.endsWith('.pdf');
    const isDoc = item.endsWith('.doc') || item.endsWith('.docx');
    const isXlsx = item.endsWith('.xlsx') || item.endsWith('.xls');

    const getFileThumbnail = () => {
      if (isPdf) return PdfImage;
      if (isDoc) return DocImage;
      if (isXlsx) return XlsxImage;
      return null;
    };

    const fileThumbnail = getFileThumbnail();
    const fileName = item.split('/').pop();

    return (
      <div style={{ 
        display: 'inline-block', 
        margin: '8px', 
        position: 'relative',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        padding: '8px',
        backgroundColor: '#fafafa'
      }}>
        <Popconfirm
          title="Delete Document"
          description="Are you sure you want to delete this document?"
          onConfirm={() => deleteImage(recordId, item)}
          okText="Yes"
          cancelText="No"
          okType="danger"
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              zIndex: 10,
              backgroundColor: '#fff',
              border: '1px solid #ff4d4f'
            }}
          />
        </Popconfirm>

        {fileThumbnail ? (
          <div style={{ textAlign: 'center' }}>
            <a
              href={item}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <img
                src={fileThumbnail}
                alt={fileName}
                style={{ 
                  width: 60, 
                  height: 60, 
                  objectFit: 'cover',
                  borderRadius: '4px',
                  marginBottom: '4px'
                }}
              />
              <div style={{ 
                fontSize: '10px', 
                color: '#666',
                maxWidth: 60,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {fileName}
              </div>
            </a>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Image
              width={60}
              height={60}
              src={item}
              style={{
                objectFit: 'cover',
                borderRadius: '4px',
                marginBottom: '4px'
              }}
              preview={{
                onVisibleChange: setPreviewVisible,
              }}
            />
            <div style={{ 
              fontSize: '10px', 
              color: '#666',
              maxWidth: 60,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {fileName}
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Occupation',
      dataIndex: 'occ_name',
      key: 'occ_name',
      render: (text) => (
        <div>
          <Text strong>{text || '-'}</Text>
        </div>
      ),
    },
    {
      title: 'Field',
      dataIndex: 'occ_field',
      key: 'occ_field',
      render: (text) => text || '-',
    },
    {
      title: 'Category',
      dataIndex: 'occ_category',
      key: 'occ_category',
      render: (text) => text || '-',
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {record.shop_no && `Shop: ${record.shop_no}`}
            {record.building_name && `, ${record.building_name}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {[record.occ_locality, record.occ_district, record.occ_state].filter(Boolean).join(', ') || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.occ_office_phone_no && (
            <div style={{ fontSize: '12px' }}>
              <PhoneOutlined /> {record.occ_office_phone_no}
            </div>
          )}
          {record.occ_mobile_no && (
            <div style={{ fontSize: '12px' }}>
              📱 {record.occ_mobile_no}
            </div>
          )}
          {!record.occ_office_phone_no && !record.occ_mobile_no && '-'}
        </div>
      ),
    },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => {
        const docs = record.occ_documents ? JSON.parse(record.occ_documents) : [];
        return (
          <Badge count={docs.length} showZero>
            <Button 
              type="text" 
              icon={<FileImageOutlined />} 
              size="small"
            />
          </Badge>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => showDeleteConfirm(record)}
            />
          </Tooltip> */}
                <div className="button-container">
                    <Space size="middle">
                        <button className="update-btn" onClick={() => handleEdit(record)}> <EditOutlined /></button>
                        <button className="delete-btn" onClick={() => showDeleteConfirm(record)}><DeleteOutlined /></button>
                    </Space>
                </div>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading occupation details...</div>
      </div>
    );
  }

  if (!user_id) {
    return (
      <Alert
        message="Member Required"
        description="Please save member basic information first before adding occupation details."
        type="warning"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <Card
        size="small"
        style={{ marginBottom: 6 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BankOutlined style={{ color: '#7a0000' }} />
            <span>Occupation Details</span>
            {userData.length > 0 && (
              <Badge count={userData.length} style={{ backgroundColor: '#52c41a' }} />
            )}
          </div>
        }
        extra={
          <Space>
            {viewMode === 'table' && userData.length > 0 && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleViewChange('form')}
                style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
              >
                Add New
              </Button>
            )}
            {viewMode === 'form' && (
              <Button
                icon={<TableOutlined />}
                onClick={() => handleViewChange('table')}
                disabled={userData.length === 0}
                style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}
              >
                View All
              </Button>
            )}
          </Space>
        }
      />

      {/* Content */}
      {viewMode === 'empty' && userData.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text type="secondary">No occupation details found</Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleViewChange('form')}
                    style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
                  >
                    Add First Occupation
                  </Button>
                </div>
              </div>
            }
          />
        </Card>
      )}

      {viewMode === 'table' && userData.length > 0 && (
        <Card styles={{ body: { padding: '8px 12px' } }}>
          <Table
            columns={columns}
            dataSource={userData}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} records`
            }}
            scroll={{ x: true }}
            expandable={{
              expandedRowKeys,
              onExpand: (expanded, record) => handleExpand(record),
              expandedRowRender: (record) => (
                <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                  <Title level={5}>
                    <FileImageOutlined /> Documents & Images
                  </Title>
                  {record.occ_documents && JSON.parse(record.occ_documents).length > 0 ? (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 8,
                      padding: '8px',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px',
                      backgroundColor: '#fff'
                    }}>
                      {JSON.parse(record.occ_documents).map((item, i) => (
                        <div key={i}>
                          {renderFilePreview(item, record.id)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty 
                      image={Empty.PRESENTED_IMAGE_SIMPLE} 
                      description="No documents uploaded"
                      style={{ margin: '20px 0' }}
                    />
                  )}
                </div>
              ),
            }}
            rowKey="id"
          />
        </Card>
      )}

      {viewMode === 'form' && (
        <Card
          styles={{ body: { padding: '6px 10px' } }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isUpdateMode ? <EditOutlined /> : <PlusOutlined />}
              <span>{isUpdateMode ? 'Edit Occupation Details' : 'Add New Occupation'}</span>
            </div>
          }
          extra={
            <Space>
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleFormReset}
              >
                Clear
              </Button>
              {userData.length > 0 && (
                <Button 
                  icon={<TableOutlined />}
                  onClick={() => handleViewChange('table')}
                >
                  View All
                </Button>
              )}
            </Space>
          }
        >
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            size="small"
            className="add-payment-form compact-form"
          >
            <Row gutter={[8, 0]}>
              {/* Basic Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <BankOutlined /> Basic Information
                </Divider>
              </Col>
              
              <Col xs={24} sm={12} lg={6}>
                <Item label="Occupation" name="occ_name">
                  <AutoComplete 
                    placeholder="Enter occupation"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_name")}
                    onSearch={(text) => getPanelValue(text, "occ_name", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Occupation Field" name="occ_field">
                  <AutoComplete 
                    placeholder="e.g., Healthcare, Technology"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_field")}
                    onSearch={(text) => getPanelValue(text, "occ_field", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Category" name="occ_category">
                  <AutoComplete 
                    placeholder="e.g., Professional, Business"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_category")}
                    onSearch={(text) => getPanelValue(text, "occ_category", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Specification" name="other_spec">
                  <Input placeholder="Other details" />
                </Item>
              </Col>

              {/* Address Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <EnvironmentOutlined /> Business Address
                </Divider>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Shop No./Plot" name="shop_no">
                  <Input placeholder="Shop number or plot" />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Building Name" name="building_name">
                  <Input placeholder="Building or complex name" />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Street" name="occ_street">
                  <Input placeholder="Street address" />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Locality/Area" name="occ_locality">
                  <Input placeholder="Locality or area" />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="District" name="occ_district">
                  <AutoComplete 
                    placeholder="District"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_district")}
                    onSearch={(text) => getPanelValue(text, "occ_district", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="State" name="occ_state">
                  <AutoComplete 
                    placeholder="State"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_state")}
                    onSearch={(text) => getPanelValue(text, "occ_state", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item label="Country" name="occ_country">
                  <AutoComplete 
                    placeholder="Country"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "occ_country")}
                    onSearch={(text) => getPanelValue(text, "occ_country", "sn_occupation")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item 
                  label="Pincode" 
                  name="occ_zip_code"
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: 'Please enter a valid pincode!',
                    },
                  ]}
                >
                  <Input placeholder="Pincode" />
                </Item>
              </Col>

              {/* Contact Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <PhoneOutlined /> Contact Information
                </Divider>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item 
                  label="Office Phone" 
                  name="occ_office_phone_no"
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: 'Please enter a valid phone number!',
                    },
                  ]}
                >
                  <Input 
                    placeholder="Office phone number" 
                    prefix={<PhoneOutlined />}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Item 
                  label="Mobile Number" 
                  name="occ_mobile_no"
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: 'Please enter a valid mobile number!',
                    },
                  ]}
                >
                  <Input 
                    placeholder="Mobile number" 
                    prefix={<PhoneOutlined />}
                  />
                </Item>
              </Col>

              {/* Documents Upload */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <FileImageOutlined /> Documents & Images
                </Divider>
              </Col>

              <Col xs={24}>
                <Item label="Upload Documents">
                  <Upload.Dragger
                    name="files"
                    multiple
                    beforeUpload={beforeUpload}
                    fileList={fileList.map(file => ({
                      uid: file.uid || file.name,
                      name: file.name,
                      status: 'done',
                    }))}
                    onRemove={(file) => {
                      setFileList(prevState => prevState.filter(item => (item.uid || item.name) !== (file.uid || file.name)));
                    }}
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag files to upload
                    </p>
                    <p className="ant-upload-hint">
                      Support for images, PDFs, Word documents, and Excel files. Maximum file size: 10MB
                    </p>
                  </Upload.Dragger>
                </Item>
              </Col>

              {/* File Preview */}
              {fileList.length > 0 && (
                <Col xs={24}>
                  <Card 
                    size="small" 
                    title={`Selected Files (${fileList.length})`}
                    style={{ backgroundColor: '#f6ffed' }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {fileList.map((file, index) => (
                        <div 
                          key={file.uid || file.name}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            gap: 8
                          }}
                        >
                          {getFileIcon(file.name)}
                          <span style={{ fontSize: '12px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </span>
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setFileList(prevState => prevState.filter((_, i) => i !== index));
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              )}

              {/* Action Buttons */}
              <Col xs={24}>
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                  {isUpdateMode && selectedRecord && (
                    <span style={{ fontSize: 12, color: '#8c8c8c', marginRight: 'auto' }}>
                      Editing: {selectedRecord.occ_name || 'Unnamed Occupation'}
                    </span>
                  )}
                  {userData.length > 0 && (
                    <Button icon={<TableOutlined />} onClick={() => handleViewChange('table')} disabled={saving} style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}>
                      View All Records
                    </Button>
                  )}
                  <Button icon={<ClearOutlined />} onClick={handleFormReset} disabled={saving} style={{ borderRadius: 8 }}>
                    Clear Form
                  </Button>
                  <Button type="primary" icon={saving ? null : <SaveOutlined />} loading={saving} onClick={() => form.submit()} style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, minWidth: 130 }}>
                    {saving ? 'Saving...' : (isUpdateMode ? 'Update Details' : 'Save Details')}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      {/* Image Preview Modal */}
      <Modal
        open={previewVisible}
        title="Image Preview"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default OccupationDetails;